import { useCallback, useEffect, useState } from 'react';
import { Globe, Plus, Check, X, Trash2 } from 'lucide-react';
import { api } from '@/services/api';
import { SslStatusBadge, DnsVerifiedBadge } from '@/components/StatusBadge';
import type { Domain } from '@/types';

export function DomainsTab({ siteId, isAdmin }: { siteId: string; isAdmin: boolean }) {
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [verifying, setVerifying] = useState('');

  const fetchDomains = useCallback(async () => {
    try {
      const data = await api.get<Domain[]>(`/sites/${siteId}/domains`);
      setDomains(data);
    } catch {
      /* */
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    void fetchDomains();
  }, [fetchDomains]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post(`/sites/${siteId}/domains`, { domain: newDomain });
      setNewDomain('');
      setShowAdd(false);
      await fetchDomains();
    } catch {
      /* */
    } finally {
      setAdding(false);
    }
  };

  const handleVerify = async (domainId: string) => {
    setVerifying(domainId);
    try {
      await api.post(`/sites/${siteId}/domains/${domainId}/verify`);
      await fetchDomains();
    } catch {
      /* */
    } finally {
      setVerifying('');
    }
  };

  const handleDelete = async (domainId: string) => {
    try {
      await api.delete(`/sites/${siteId}/domains/${domainId}`);
      await fetchDomains();
    } catch {
      /* */
    }
  };

  const handleSetPrimary = async (domainId: string) => {
    try {
      await api.patch(`/sites/${siteId}/domains/${domainId}`, { isPrimary: true });
      await fetchDomains();
    } catch {
      /* */
    }
  };

  if (loading) {
    return <div className="flex h-32 items-center justify-center"><div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" /></div>;
  }

  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setShowAdd(!showAdd)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-xs font-medium text-white hover:bg-brand-700 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            Domain hinzufügen
          </button>
        </div>
      )}

      {showAdd && (
        <form onSubmit={handleAdd} className="flex gap-2 rounded-xl border border-surface-300 bg-surface-100 p-4">
          <input
            type="text"
            required
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            placeholder="example.com"
            className="flex-1 rounded-lg border border-surface-400 bg-surface-200 px-3 py-2 text-sm text-neutral-200 placeholder-neutral-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <button
            type="submit"
            disabled={adding}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
          >
            {adding ? 'Hinzufügen…' : 'Hinzufügen'}
          </button>
          <button
            type="button"
            onClick={() => setShowAdd(false)}
            className="rounded-lg px-3 py-2 text-sm text-neutral-400 hover:text-neutral-200 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </form>
      )}

      {domains.length === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-surface-300 bg-surface-100 py-12">
          <Globe className="mb-2 h-8 w-8 text-neutral-600" />
          <p className="text-sm text-neutral-500">Keine Domains konfiguriert.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-surface-300 bg-surface-100">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-surface-300 text-xs uppercase text-neutral-500">
                <th className="px-5 py-3 font-medium">Domain</th>
                <th className="px-5 py-3 font-medium">Primary</th>
                <th className="px-5 py-3 font-medium">DNS</th>
                <th className="px-5 py-3 font-medium">SSL</th>
                <th className="px-5 py-3 font-medium">TXT Record</th>
                {isAdmin && <th className="px-5 py-3 font-medium">Aktionen</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-300">
              {domains.map((d) => (
                <tr key={d.id} className="hover:bg-surface-200/50 transition-colors">
                  <td className="px-5 py-3 font-medium text-neutral-200">{d.domain}</td>
                  <td className="px-5 py-3">
                    {d.isPrimary ? (
                      <Check className="h-4 w-4 text-green-400" />
                    ) : isAdmin ? (
                      <button
                        type="button"
                        onClick={() => handleSetPrimary(d.id)}
                        className="text-xs text-neutral-500 hover:text-brand-400 transition-colors"
                      >
                        Als Primary
                      </button>
                    ) : (
                      <span className="text-neutral-600">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3"><DnsVerifiedBadge verified={d.dnsVerified} /></td>
                  <td className="px-5 py-3"><SslStatusBadge status={d.sslStatus} /></td>
                  <td className="px-5 py-3">
                    {d.txtRecord ? (
                      <code className="rounded bg-surface-300 px-2 py-0.5 text-xs text-neutral-400 break-all">
                        {d.txtRecord}
                      </code>
                    ) : '—'}
                  </td>
                  {isAdmin && (
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleVerify(d.id)}
                          disabled={d.dnsVerified || verifying === d.id}
                          className="text-xs text-brand-400 hover:text-brand-300 disabled:opacity-40 transition-colors"
                        >
                          {verifying === d.id ? 'Prüfe…' : 'Verifizieren'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(d.id)}
                          className="text-neutral-500 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
